import { omitBy } from 'lodash';
import base from './baseApi';
import { createSearchParams } from 'react-router';
import { driveTimesParams } from 'src/utils/driveTime.util';
const { get, post, patch } = base();

export const searchRegion = (payload: any) => {
   return post('region/search', payload);
};

export const searchHotel = (payload: any, signal?: AbortSignal) => {
   // `signal` must be forwarded: without it a superseded search runs to completion against the
   // 180s global timeout and its result is silently discarded (NPT-1500).
   return post(`hotel/serp`, payload, {}, undefined, signal);
};

export type DriveTimesResponse = {
   status: 'ready' | 'pending' | 'unavailable';
   driveTimes: Record<string, { seconds: number; meters: number }>;
};

/** NPT-2175. Poll target for a `pending` serp; public + throttled, so callers must not spam it. */
export const getDriveTimes = (geohash: string, hotelIds: string[], signal?: AbortSignal) =>
   get('/hotel/drive-times', driveTimesParams(geohash, hotelIds), undefined, undefined, signal);

export const getStaticHotels = (payload: any) => {
   return post(`hotel/static-list`, payload);
};

export const getHotelDetail = (id: any, query: any, payload: any) => {
   const initParams: any = omitBy(query, v => v === '');
   const params = createSearchParams(initParams);
   return post(`/hotel/${id}/retrieve?${params.toString()}`, payload);
};

export const getHotelPreview = (id: string) => {
   return get(`/hotel/${id}/preview`);
};

export const getHotelRates = (id: any, query: any, payload: any) => {
   const initParams: any = omitBy(query, v => v === '');
   const params = createSearchParams(initParams);
   return post(`/hotel/${id}/rates?${params.toString()}`, payload);
};

/**
 * NPT-2115 — the cheapest fully refundable sibling of a checkout rate (same room type and meal
 * plan, same BH search session), priced for the viewing org. `upgrade` is null with a `reason`
 * when there is nothing to offer; the caller shows nothing in that case.
 */
export type RefundableUpgradeResponse = {
   searchId: number | string;
   current?: { blockId: string; totalPrice: number; refundability: { kind: 'FULL' | 'PARTIAL' | 'NONE' } };
   upgrade: {
      room: Record<string, any>;
      totalPrice: number;
      priceDelta: number;
      freeCancellationUntil: string | null;
   } | null;
   reason: 'SESSION_EXPIRED' | 'RATE_NOT_FOUND' | 'ALREADY_REFUNDABLE' | 'NO_REFUNDABLE_OPTION' | null;
};

export const getRefundableUpgrade = (payload: { hotelId: number; searchId: number | string; blockId: string }) => {
   return post('/hotel/refundable-upgrade', payload);
};

export const getSimilarHotels = (id: any, query: any, payload: any) => {
   const initParams: any = omitBy(query, v => v === '');
   const params = createSearchParams(initParams);
   return post(`/hotel/${id}/similar-hotel?${params.toString()}`, payload);
};

export const getHotelCommission = ({ id, payload }: any) => {
   return post(`/hotel/${id}/commission`, payload);
};

export const getHotelImages = (ids: string[]) => {
   return post('/hotel/images', ids);
};

export const getHotelRatings = ({ countryCode, hotels }: any) => {
   const params = createSearchParams({ countryCode });
   return post(`/hotel/ratings?${params}`, hotels);
};

export const getHotelReviews = ({ id, ratingId, nextPageToken, sortBy }: any) => {
   return post(`/hotel/${id}/reviews`, { ratingId, nextPageToken, sortBy });
};

export const getHotelStatic = () => get(`/hotel/static`);

export const updateRatingData = (hotelId: string, payload: any) => {
   return patch(`/static/rating/${hotelId}/update`, payload);
}
